/**
 * Tariff rates on PO lines. Rates are fractions everywhere they are stored
 * (0.075 = 7.5%) and percentages everywhere a person types or reads them.
 */

import { CatalogTariffs } from "services/InventoryService";

export const toPercent = (rate: number | null | undefined): number | null =>
  rate == null ? null : Math.round(rate * 10000) / 100;
export const fromPercent = (percent: number | null | undefined): number | null =>
  percent == null ? null : Math.round(percent * 100) / 10000;
export const formatPercent = (rate: number | null | undefined): string =>
  rate == null ? "—" : `${toPercent(rate)}%`;

type RatedLine = { quantity: number; unitPrice?: number | null; tariffRate?: number | null };

/**
 * The PO's rate: each line's rate weighted by its value (quantity × unit price),
 * or by quantity alone when any line is unpriced. Lines without a rate are left
 * out, so a partly-rated PO takes the rated lines' average for its whole amount.
 * Mirrors weightedTariffRate on the server.
 */
export function weightedTariffRate(lines: RatedLine[]): number | null {
  const rated = lines.filter(li => li.tariffRate != null && li.quantity > 0);
  if (rated.length === 0) return null;
  const priced = rated.every(li => li.unitPrice != null);
  const weightOf = (li: RatedLine) => (priced ? li.quantity * (li.unitPrice as number) : li.quantity);
  const total = rated.reduce((sum, li) => sum + weightOf(li), 0);
  if (total <= 0) return null;
  return Math.round((rated.reduce((sum, li) => sum + weightOf(li) * (li.tariffRate as number), 0) / total) * 1e6) / 1e6;
}

export type TariffSuggestion = { rate: number; source: string };

/** Where a blank tariff cell should start: the product's own rate, else the supplier's default, else what the last PO used. */
export function suggestedTariffFor(
  tariffs: CatalogTariffs | null | undefined,
  inventoryId: string | null | undefined,
  supplierId: number | null | undefined,
): TariffSuggestion | undefined {
  if (!tariffs) return undefined;
  const supplierKey = supplierId != null ? String(supplierId) : null;
  if (inventoryId && tariffs.items[inventoryId] != null) return { rate: tariffs.items[inventoryId], source: "the SKU master's rate for this product" };
  if (supplierKey && tariffs.suppliers[supplierKey] != null) return { rate: tariffs.suppliers[supplierKey], source: "the supplier's default rate" };
  if (inventoryId && tariffs.recentByItem[inventoryId] != null) return { rate: tariffs.recentByItem[inventoryId], source: "the rate on the last PO for this product" };
  if (supplierKey && tariffs.recentBySupplier[supplierKey] != null) return { rate: tariffs.recentBySupplier[supplierKey], source: "the rate on the supplier's last PO" };
  return undefined;
}

/** Lines still missing a tariff rate — a reminder, not a blocker. */
export function linesWithoutTariff(lines: Array<{ inventoryId: string; tariffRate?: number | null }>): number {
  return lines.filter(li => li.inventoryId && li.tariffRate == null).length;
}
